import {
  BogoOfferPayload,
  CartItemType,
  CartLoyaltyV2SliceType,
  OfferPayload,
} from "../../../services/redux/types/CartLoyaltyV2Types";
import { replaceNullWithUndefined } from "../../../services/redux/types/TypeHelper";
import CartItem from "../../CartItem";
import { MAIN_OFFER_TYPE } from "../../enums";
import { getAllUnavailableItemIds } from "../../help/itemDetails/itemDetails";
import { DoubleLinkedList } from "../../help/linkedList/LinkedList";
import { nextBogoStep } from "../../help/offers/offers";
import {
  AddonGroupType,
  BogoOfferDetailsType,
  BogoOfferType,
  ItemType,
  OfferTypeV2,
  SubItemType,
} from "../../types";
import { ItemOffer } from "../ItemOffer/ItemOffer";
import Offer from "../Offer/Offer";
import { createOffer } from "../OfferClassUtils/OfferCreator/offerCreator";
import { IsOfferApplicableArgs } from "../types";

const lodashClonedeep = require("lodash.clonedeep");

class BogoOffer extends Offer {
  offerType: MAIN_OFFER_TYPE.BOGO;
  details: BogoOfferDetailsType;
  selectedBuySubItem: SubItemType | undefined;
  getOffer: Offer;

  constructor(offer: BogoOfferType) {
    super(offer);
    this.details = offer.details;
    this.offerType = MAIN_OFFER_TYPE.BOGO;
    this.isCartV2 = true;
    this.getOffer = createOffer(this.details.getPackageOffer);
  }

  // Calculate the number of steps for the BOGO offer
  calculateBogoSteps(): number {
    let totalSteps = 1; // Buy Item Customization

    if (this.details.buySubItems.length > 1) {
      totalSteps += 1; // Choose Buy Item
    }

    totalSteps += this.getOffer.numberOfSteps(); // Get Sub Offer number of steps

    return totalSteps;
  }

  applyOffer(
    cartV2: CartLoyaltyV2SliceType,
    cartItem?: CartItem[],
    subOffer?: OfferPayload,
    extraDetails?: { isNotFirstApply?: boolean }
  ): BogoOfferPayload | null {
    cartV2.selectedParentOffer = this as OfferTypeV2;

    if (!cartItem || cartItem.length === 0) {
      // Start of BOGO
      if (!extraDetails?.isNotFirstApply) {
        const totalSteps = this.calculateBogoSteps();
        cartV2.bogoSteps = {
          totalSteps: totalSteps,
          currentStep: 0,
        };
      }

      // Handle multiple buy items
      if (this.details.buySubItems.length > 1) {
        cartV2.redirectToItem = {
          itemFromListOffer: this,
        };
        cartV2.selectedOfferId = this.id;

        return null;
      }

      const prevSelectedBuySubItemId =
        cartV2.selectedBuySubItem?.subItemDetails.id;
      const currentSelectedBuySubItemId = this.details.buySubItems[0].id;

      const buySubItem = this.details.buySubItems[0];

      const itemData = this.itemWithoutAddons(buySubItem, true);
      let buyCartItemWithoutAddons = itemData.toJson();
      buyCartItemWithoutAddons = replaceNullWithUndefined(
        itemData
      ) as CartItemType;

      if (prevSelectedBuySubItemId !== currentSelectedBuySubItemId) {
        cartV2.selectedBuySubItem = buyCartItemWithoutAddons;
      }

      // Redirect to Buy Item Customization
      cartV2.redirectToItem = {
        itemId: buySubItem.itemId!,
        subItemId: buySubItem.id!,
      };

      cartV2.selectedOfferId = this.id;
      cartV2.selectedOfferSubItems = new DoubleLinkedList<CartItemType>();

      return null;
    }

    const buyCartItem = cartItem[0];
    const buySubItem = this.details.buySubItems.find(
      (subItem) => subItem.id === buyCartItem.subItemDetails.id
    );

    if (buySubItem) {
      cartV2.selectedBuySubItem = buyCartItem as CartItemType;
    }

    let offerPayload: OfferPayload | null = null;

    if (subOffer) {
      offerPayload = subOffer;
    } else {
      this.getOffer.isSubOffer = true;

      const redeemedOffer = cartV2.cart.redeemedOffer as BogoOfferPayload;
      const isEditing =
        cartV2.cart.redeemedOffer?.id === this.id && !!redeemedOffer;

      if (!isEditing && this.getOffer.isItemFromListOffer()) {
        cartV2.bogoSteps = nextBogoStep(cartV2.bogoSteps); // Increment BOGO step
        cartV2.redirectToItem = {
          itemFromListOffer: this.getOffer,
        };
        cartV2.selectedOfferId = this.getOffer.id;

        return null;
      }

      const getOfferToRedeem = lodashClonedeep(this.getOffer) as Offer;
      if (
        isEditing &&
        getOfferToRedeem instanceof ItemOffer &&
        getOfferToRedeem.isItemFromListOffer()
      ) {
        const redeemedGetOffer = redeemedOffer?.getOffer;

        let redeemedCartItem: CartItemType | undefined;
        if ("item" in redeemedGetOffer) {
          redeemedCartItem = redeemedGetOffer.item;
        }
        const getOfferSubItems = getOfferToRedeem.details.subItems;

        const getOfferSubItem = getOfferSubItems.find(
          (subItem) => subItem.id === redeemedCartItem?.subItemDetails.id
        );

        if (getOfferSubItem) {
          getOfferToRedeem.details.subItems = [getOfferSubItem];
        }
      }

      offerPayload = getOfferToRedeem.applyOffer(cartV2);

      if (!offerPayload) {
        return null;
      }
    }

    const bogoOfferPayload = {
      id: this.id,
      offerType: this.offerType,
      offerCategory: this.offerCategory,
      details: replaceNullWithUndefined(this.details),
      uiDetails: this.uiDetails,
      buyItem: replaceNullWithUndefined(buyCartItem) as CartItemType,
      getOffer: offerPayload!,
    };

    return bogoOfferPayload;
  }

  itemWithoutAddons(subItem: SubItemType, isBuyItem?: boolean): CartItem {
    const itemData = new CartItem(
      "",
      1,
      0,
      {
        ...subItem,
        customFranchiseSubItemPrices:
          subItem.customFranchiseSubItemPrices ?? [],
      },
      { ...subItem.item!, subItems: [] },
      undefined,
      -1,
      undefined,
      undefined,
      (subItem.item as ItemType).customDeliveryTime,
      (subItem.item as ItemType).excludeFromOffers,
      isBuyItem,
      false
    );

    itemData.subItemDetails.item = {
      ...itemData.subItemDetails.item!,
      subItems: [],
    };
    const itemWithEmptyAddons = lodashClonedeep(itemData.subItemDetails);
    itemWithEmptyAddons.allAddonGroups?.forEach(
      (addonGroup: AddonGroupType) => {
        addonGroup.addonGroupSubItem = [];
      }
    );
    itemWithEmptyAddons.basePrice = itemWithEmptyAddons.price;
    itemWithEmptyAddons.previousPrice = -1;

    itemData.subItemDetails = itemWithEmptyAddons;

    return itemData;
  }

  isBogoOffer() {
    return true;
  }

  isOfferApplicableByType(args: IsOfferApplicableArgs): boolean {
    return this.isBogoOfferApplicable(args);
  }

  isBogoOfferApplicable(args: IsOfferApplicableArgs) {
    const isGetOfferApplicable = this.getOffer.isOfferApplicable(args);

    if (!isGetOfferApplicable) {
      return false;
    }

    const { allUnavailableItemIds } = getAllUnavailableItemIds(
      args.itemStatusRedux,
      args.orderType
    );

    if (
      this.details.buySubItems.every((subItem) =>
        allUnavailableItemIds.includes(subItem.item?.id!)
      )
    ) {
      return false;
    }

    return true;
  }

  hasDeletedItems() {
    return this.details.buySubItems.every(
      (subItem) => subItem.deletedAt || subItem.item?.deletedAt
    );
  }

  // -- TEXT FUNCTIONS SECTION - START --

  getWelcomePopupOfferText() {
    const orderNowText = this.retrieveI18ValueByKey("referral:order-now");

    return `${orderNowText}`;
  }
}

export { BogoOffer };
